import type {
  WorkforceProfileCompetencyRow,
  WorkforceProfileHighlights,
  WorkforceProfileSeniorityLevelAssessment,
} from './workforceProfilesSharedTypes';
import type {CompanyWorkforceProfile} from './workforceProfilesTypes';
import type {BilingualStringList, LocalisedPropertyValue, RequiredLanguageProfile} from '../../../../types/shared-types';

export interface WorkforceProfileExtractedSkill {
  skill: string;
  evidence: string;
  confidence?: string | null;
}

export interface WorkforceProfileExtractedLanguage {
  language: string;
  level: string;
  evidence: string;
}

export interface WorkforceProfileExtractedWorkExperience {
  roleTitle: string;
  organizationDescriptor: string;
  duration: string;
  highlights: string[];
}

export interface WorkforceProfileExtractedCandidateProfile {
  summary?: string | null;
  education?: string | null;
  responsibilities?: string | null;
  senioritySignals?: string | null;
  industryExperience?: string | null;
  workingStyleNotes?: string | null;
}

export interface WorkforceProfileInferredCulturalScaleConfiguration {
  minValue: number;
  maxValue: number;
  step: number;
  labelLeft: RequiredLanguageProfile;
  labelRight: RequiredLanguageProfile;
}

export interface WorkforceProfileInferredCulturalMethodItem {
  resourceId: string;
  humanReadableIdentifier: string;
  title: RequiredLanguageProfile;
  scaleConfiguration: WorkforceProfileInferredCulturalScaleConfiguration;
}

export interface WorkforceProfileInferredCulturalAnswer {
  key: string;
  value: number;
  rationale: RequiredLanguageProfile | null;
  methodItem: WorkforceProfileInferredCulturalMethodItem | null;
}

export interface WorkforceProfileCulturalFitSummary {
  fitScore: number | null;
  averageCompanyEmployeeFit: number | null;
  averageCompanyApplicantFit: number | null;
}

export interface WorkforceProfileCulturalFitPillarScore {
  title: RequiredLanguageProfile;
  companyScore: number;
  workforceProfileScore: number;
}

export type WorkforceAlignmentLabel = 'aligned' | 'close' | 'moderate' | 'different';

const WORKFORCE_ALIGNMENT_LABELS: WorkforceAlignmentLabel[] = ['aligned', 'close', 'moderate', 'different'];

export const isWorkforceAlignmentLabel = (value: string): value is WorkforceAlignmentLabel =>
  WORKFORCE_ALIGNMENT_LABELS.includes(value as WorkforceAlignmentLabel);

export interface WorkforceProfileCulturalFitTopQuestion {
  resourceId: string;
  humanReadableIdentifier: string;
  title: RequiredLanguageProfile;
  description: RequiredLanguageProfile | null;
  overlap: number | null;
  overlapLabel: WorkforceAlignmentLabel;
  companyValue: number;
  workforceProfileValue: number;
  scaleConfiguration: WorkforceProfileInferredCulturalScaleConfiguration;
}

export interface WorkforceProfileCulturalFitPillarDescription {
  pillarTitle: RequiredLanguageProfile;
  overlap: number | null;
  evaluations: BilingualStringList;
  opportunities: BilingualStringList;
  risks: BilingualStringList;
  whatToLookFor: BilingualStringList;
  interviewQuestions: BilingualStringList;
}

export interface WorkforceProfileCulturalFitAnalysis {
  available: boolean;
  cultureFit: WorkforceProfileCulturalFitSummary | null;
  cultureType: LocalisedPropertyValue | null;
  pillarData: WorkforceProfileCulturalFitPillarScore[];
  topQuestions: WorkforceProfileCulturalFitTopQuestion[];
  pillarDescriptions: WorkforceProfileCulturalFitPillarDescription[];
}

export interface WorkforceProfileAnalysisDataInput {
  extractionConfidence?: string | null;
  candidateProfile?: WorkforceProfileExtractedCandidateProfile;
  skillsCompetencies: WorkforceProfileExtractedSkill[];
  knowledgeSkills: WorkforceProfileExtractedSkill[];
  workExperiences: WorkforceProfileExtractedWorkExperience[];
  languages: WorkforceProfileExtractedLanguage[];
  certifications: string[];
  gapsOrAmbiguities: string[];
}

export interface CareerCoachResource {
  title: string;
  type: 'book' | 'course' | 'certification' | 'practice';
  authorOrProvider?: string | null;
}

export interface CareerCoachRecommendedAction {
  action: RequiredLanguageProfile;
  resource?: CareerCoachResource | null;
  timeline?: RequiredLanguageProfile | null;
}

export type CareerCoachPriorityGapType =
  | 'core_competency'
  | 'hard_skill'
  | 'soft_skill'
  | 'responsibility'
  // Legacy values from the v1 career-coach schema, tolerated during soak.
  | 'competency'
  | 'knowledge';

export interface CareerCoachPriorityGap {
  skill: RequiredLanguageProfile;
  type: CareerCoachPriorityGapType;
  currentEvidence: RequiredLanguageProfile;
  targetExpectation: RequiredLanguageProfile;
  recommendedActions: CareerCoachRecommendedAction[];
}

export interface CareerCoachMilestone {
  month: number;
  goal: RequiredLanguageProfile;
}

export interface CareerCoachDevelopmentPlan {
  summary: RequiredLanguageProfile;
  priorityGaps: CareerCoachPriorityGap[];
  milestones: CareerCoachMilestone[];
}

export interface CareerCoachMarketTrend {
  trend: RequiredLanguageProfile;
  relevance: RequiredLanguageProfile;
  recommendation: RequiredLanguageProfile;
}

export interface CareerCoachEmergingSkill {
  skill: RequiredLanguageProfile;
  rationale: RequiredLanguageProfile;
  resource?: CareerCoachResource | null;
}

export interface CareerCoachFutureProofing {
  summary: RequiredLanguageProfile;
  marketTrends: CareerCoachMarketTrend[];
  emergingSkills: CareerCoachEmergingSkill[];
}

export interface CareerCoachAnalysis {
  available: boolean;
  reason?: string | null;
  developmentPlan?: CareerCoachDevelopmentPlan | null;
  futureProofing?: CareerCoachFutureProofing | null;
}

// Version-evolution row diffing is still computed by the BE against the legacy v1 section
// names only (core competencies / hard skills), regardless of the v2 alignment schema.
export const WORKFORCE_VERSION_EVOLUTION_COMPETENCY_SECTIONS = [
  'skills_competencies_alignment',
  'must_have_skill_fit',
  'nice_to_have_skill_fit',
] as const;

export const WORKFORCE_VERSION_EVOLUTION_KNOWLEDGE_SECTIONS = ['knowledge_skills_alignment'] as const;

export type WorkforceProfileVersionEvolutionSection =
  | (typeof WORKFORCE_VERSION_EVOLUTION_COMPETENCY_SECTIONS)[number]
  | (typeof WORKFORCE_VERSION_EVOLUTION_KNOWLEDGE_SECTIONS)[number]
  | string;

export interface WorkforceProfileVersionEvolutionSkillRef {
  skill: RequiredLanguageProfile;
  section: string;
}

export interface WorkforceProfileVersionEvolutionScoreDelta {
  delta: number | null;
  current: number | null;
  previous: number | null;
}

export type WorkforceProfileVersionEvolutionScoreDeltaKey =
  | 'alignmentScore'
  | 'culturalFitScore'
  | 'knowledgeSkillsAlignment'
  | 'responsibilitiesAlignment'
  | 'skillsCompetenciesAlignment';

export type WorkforceProfileVersionEvolutionScoreDeltas = Partial<
  Record<WorkforceProfileVersionEvolutionScoreDeltaKey, WorkforceProfileVersionEvolutionScoreDelta>
>;

export interface TargetProfileAlignmentAssessmentScope {
  representative: boolean;
  skillsTotal: number;
  skillsInScope: number;
  coreTotal: number;
  coreInScope: number;
  hardTotal: number;
  hardInScope: number;
  softTotal: number;
  softInScope: number;
  responsibilityTotal: number;
  responsibilityInScope: number;
}

export interface WorkforceProfileVersionEvolution {
  available: boolean;
  summary: string[];
  baselineVersion: number | null;
  currentVersion: number | null;
  scoreDeltas: WorkforceProfileVersionEvolutionScoreDeltas;
  newlyMetSkills: WorkforceProfileVersionEvolutionSkillRef[];
  closedGaps: WorkforceProfileVersionEvolutionSkillRef[];
  newGaps: WorkforceProfileVersionEvolutionSkillRef[];
  regressions: WorkforceProfileVersionEvolutionSkillRef[];
}

export interface CompanyWorkforceProfileDetail extends CompanyWorkforceProfile {
  targetProfileAlignmentAssessmentScope?: TargetProfileAlignmentAssessmentScope | null;
  summary?: string | null;
  highlights?: WorkforceProfileHighlights;
  coreCompetencyRows: WorkforceProfileCompetencyRow[];
  hardSkillRows: WorkforceProfileCompetencyRow[];
  toolRows: WorkforceProfileCompetencyRow[];
  domainKnowledgeRows: WorkforceProfileCompetencyRow[];
  softSkillRows: WorkforceProfileCompetencyRow[];
  responsibilitiesRows: WorkforceProfileCompetencyRow[];
  responsibilitiesExplanation?: string | null;
  seniorityLevelAssessment?: WorkforceProfileSeniorityLevelAssessment | null;
  processingError?: string | null;
  profileAnalysisDataInput?: WorkforceProfileAnalysisDataInput;
  inferredCulturalAnswers?: WorkforceProfileInferredCulturalAnswer[];
  culturalFitAnalysis?: WorkforceProfileCulturalFitAnalysis;
  careerCoachAnalysis?: CareerCoachAnalysis;
  versionEvolution?: WorkforceProfileVersionEvolution | null;
}
